import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Shield,
  Building2,
  Plus,
  Loader2,
  FileText,
  Clock,
  CheckCircle,
  Settings,
  Globe,
  Lock,
  Edit,
  Trash2,
  ArrowLeft,
  LogOut,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompliancePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [activeTab, setActiveTab] = useState("profiles");
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  
  // New profile form
  const [newProfile, setNewProfile] = useState({
    name: "",
    code: "",
    description: "",
    is_default: false,
    rules: {
      minutes: {
        require_quorum_explicit: true,
        require_end_time: true,
        require_chair_signature_for_final: true,
        min_retention_years_minutes: 10,
        require_attendance_confirmation: true,
        require_agenda_approval: false,
      },
      artifacts: {
        retain_audio_months: 12,
        retain_transcript_months: 60,
        retain_attachments_months: 60,
        retain_exports_months: 120,
      },
      sections: {
        require_matters_arising_if_prior_exists: true,
        enable_resolutions_section: "auto",
        require_action_items_section: true,
        enable_declarations_section: "auto",
      },
    },
  });

  useEffect(() => {
    if (user && user.role !== "platform_admin") {
      toast.error("Access denied. Platform admin only.");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [profilesRes, companiesRes] = await Promise.all([
        axios.get(`${API}/platform/compliance-profiles`),
        axios.get(`${API}/platform/companies`).catch(() => ({ data: { companies: [] } })),
      ]);
      setProfiles(profilesRes.data.profiles || []);
      setCompanies(companiesRes.data.companies || []);
    } catch (error) {
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/platform/compliance-profiles`, newProfile);
      toast.success("Compliance profile created");
      setShowCreateModal(false);
      setNewProfile({
        name: "",
        code: "",
        description: "",
        is_default: false,
        rules: {
          minutes: {
            require_quorum_explicit: true,
            require_end_time: true,
            require_chair_signature_for_final: true,
            min_retention_years_minutes: 10,
            require_attendance_confirmation: true,
            require_agenda_approval: false,
          },
          artifacts: {
            retain_audio_months: 12,
            retain_transcript_months: 60,
            retain_attachments_months: 60,
            retain_exports_months: 120,
          },
          sections: {
            require_matters_arising_if_prior_exists: true,
            enable_resolutions_section: "auto",
            require_action_items_section: true,
            enable_declarations_section: "auto",
          },
        },
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create profile");
    } finally {
      setCreating(false);
    }
  };

  const handleAssignCompliance = async (companyId, profileId) => {
    setAssigning(true);
    try {
      await axios.put(`${API}/platform/companies/${companyId}/compliance`, {
        compliance_profile_id: profileId,
      });
      toast.success("Compliance profile assigned");
      setShowAssignModal(false);
      setSelectedCompany(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign profile");
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm("Are you sure you want to delete this compliance profile?")) return;
    
    try {
      await axios.delete(`${API}/platform/compliance-profiles/${profileId}`);
      toast.success("Profile deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete profile");
    }
  };

  const getProfileForCompany = (company) => {
    if (!company.compliance_profile_id) return "Default";
    const profile = profiles.find(p => p.id === company.compliance_profile_id);
    return profile?.name || "Default";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/platform")}
                className="text-stone-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-700" />
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-white font-semibold">Compliance Management</h1>
                <p className="text-xs text-emerald-400">Jurisdiction Profiles & Rules</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{user?.name}</p>
                <p className="text-xs text-stone-400">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-stone-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-stone-800 border-stone-700">
            <TabsTrigger value="profiles" className="data-[state=active]:bg-emerald-600">
              <Shield className="w-4 h-4 mr-2" />
              Compliance Profiles
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-emerald-600">
              <Building2 className="w-4 h-4 mr-2" />
              Company Assignments
            </TabsTrigger>
          </TabsList>

          {/* Profiles Tab */}
          <TabsContent value="profiles" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-white">Compliance Profiles</h2>
                <p className="text-sm text-stone-400">Manage jurisdiction-specific compliance rules</p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="create-profile-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Profile
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map((profile) => (
                <Card key={profile.id} className="bg-stone-800 border-stone-700" data-testid={`profile-card-${profile.code}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-emerald-500" />
                        <CardTitle className="text-white text-lg">{profile.name}</CardTitle>
                      </div>
                      <div className="flex gap-1">
                        {profile.is_system ? (
                          <Badge variant="secondary" className="bg-stone-700 text-stone-300">
                            System
                          </Badge>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-stone-400 hover:text-white h-8 w-8 p-0"
                              onClick={() => handleDeleteProfile(profile.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <CardDescription className="text-stone-400">
                      {profile.description || `Code: ${profile.code}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-stone-500" />
                        <span className="text-stone-400">Chair signature:</span>
                        <span className={profile.rules?.minutes?.require_chair_signature_for_final ? "text-emerald-400" : "text-stone-500"}>
                          {profile.rules?.minutes?.require_chair_signature_for_final ? "Required" : "Optional"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-stone-500" />
                        <span className="text-stone-400">Minutes retention:</span>
                        <span className="text-white">
                          {profile.rules?.minutes?.min_retention_years_minutes || 7} years
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-stone-500" />
                        <span className="text-stone-400">Resolutions:</span>
                        <span className="text-white capitalize">
                          {profile.rules?.sections?.enable_resolutions_section || "auto"}
                        </span>
                      </div>
                    </div>
                    {profile.is_default && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                        Default Profile
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="mt-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-white">Company Compliance Assignments</h2>
              <p className="text-sm text-stone-400">Assign compliance profiles to companies</p>
            </div>

            <div className="bg-stone-800 border border-stone-700 rounded-lg">
              <div className="divide-y divide-stone-700">
                {companies.length > 0 ? (
                  companies.map((company) => (
                    <div
                      key={company.id}
                      className="p-4 flex items-center justify-between hover:bg-stone-700/50"
                      data-testid={`company-row-${company.id}`}
                    >
                      <div className="flex items-center gap-4">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-stone-700 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-stone-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{company.name}</p>
                          <p className="text-sm text-stone-400">{company.slug}.mymeetingspal.com</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-stone-400">Current Profile</p>
                          <p className="text-white font-medium">{getProfileForCompany(company)}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCompany(company);
                            setShowAssignModal(true);
                          }}
                          className="border-stone-600 text-stone-300 hover:bg-stone-700"
                          data-testid={`assign-btn-${company.id}`}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Change
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-500 text-center py-8">No companies yet</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Profile Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              Create Compliance Profile
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProfile} className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Profile Name</Label>
                <Input
                  value={newProfile.name}
                  onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })}
                  required
                  placeholder="e.g., Jamaica Default"
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
              <div>
                <Label className="text-stone-300">Code</Label>
                <Input
                  value={newProfile.code}
                  onChange={(e) => setNewProfile({ ...newProfile, code: e.target.value.toUpperCase() })}
                  required
                  placeholder="e.g., JAM_DEFAULT"
                  className="bg-stone-700 border-stone-600 text-white font-mono"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-stone-300">Description</Label>
              <Textarea
                value={newProfile.description}
                onChange={(e) => setNewProfile({ ...newProfile, description: e.target.value })}
                placeholder="Describe the compliance profile..."
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>

            {/* Minutes Rules */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-500" />
                Minutes Rules
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-stone-700/50 rounded-lg">
                  <Label className="text-stone-300">Require Quorum Explicit</Label>
                  <Switch
                    checked={newProfile.rules.minutes.require_quorum_explicit}
                    onCheckedChange={(val) => setNewProfile({
                      ...newProfile,
                      rules: {
                        ...newProfile.rules,
                        minutes: { ...newProfile.rules.minutes, require_quorum_explicit: val }
                      }
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-stone-700/50 rounded-lg">
                  <Label className="text-stone-300">Require End Time</Label>
                  <Switch
                    checked={newProfile.rules.minutes.require_end_time}
                    onCheckedChange={(val) => setNewProfile({
                      ...newProfile,
                      rules: {
                        ...newProfile.rules,
                        minutes: { ...newProfile.rules.minutes, require_end_time: val }
                      }
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-stone-700/50 rounded-lg">
                  <Label className="text-stone-300">Require Chair Signature</Label>
                  <Switch
                    checked={newProfile.rules.minutes.require_chair_signature_for_final}
                    onCheckedChange={(val) => setNewProfile({
                      ...newProfile,
                      rules: {
                        ...newProfile.rules,
                        minutes: { ...newProfile.rules.minutes, require_chair_signature_for_final: val }
                      }
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-stone-700/50 rounded-lg">
                  <Label className="text-stone-300">Require Agenda Approval</Label>
                  <Switch
                    checked={newProfile.rules.minutes.require_agenda_approval}
                    onCheckedChange={(val) => setNewProfile({
                      ...newProfile,
                      rules: {
                        ...newProfile.rules,
                        minutes: { ...newProfile.rules.minutes, require_agenda_approval: val }
                      }
                    })}
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-stone-300">Minutes Retention (years)</Label>
                <Input
                  type="number"
                  value={newProfile.rules.minutes.min_retention_years_minutes}
                  onChange={(e) => setNewProfile({
                    ...newProfile,
                    rules: {
                      ...newProfile.rules,
                      minutes: { ...newProfile.rules.minutes, min_retention_years_minutes: parseInt(e.target.value) || 7 }
                    }
                  })}
                  min="1"
                  max="100"
                  className="bg-stone-700 border-stone-600 text-white w-32"
                />
              </div>
            </div>

            {/* Section Rules */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <Settings className="w-4 h-4 text-emerald-500" />
                Section Rules
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-300">Resolutions Section</Label>
                  <Select
                    value={newProfile.rules.sections.enable_resolutions_section}
                    onValueChange={(val) => setNewProfile({
                      ...newProfile,
                      rules: {
                        ...newProfile.rules,
                        sections: { ...newProfile.rules.sections, enable_resolutions_section: val }
                      }
                    })}
                  >
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-700 border-stone-600">
                      <SelectItem value="auto">Auto (show if resolutions exist)</SelectItem>
                      <SelectItem value="always">Always show</SelectItem>
                      <SelectItem value="never">Never show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-stone-300">Declarations Section</Label>
                  <Select
                    value={newProfile.rules.sections.enable_declarations_section}
                    onValueChange={(val) => setNewProfile({
                      ...newProfile,
                      rules: {
                        ...newProfile.rules,
                        sections: { ...newProfile.rules.sections, enable_declarations_section: val }
                      }
                    })}
                  >
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-700 border-stone-600">
                      <SelectItem value="auto">Auto (show if declarations exist)</SelectItem>
                      <SelectItem value="always">Always show</SelectItem>
                      <SelectItem value="never">Never show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-stone-700/50 rounded-lg">
              <Label className="text-stone-300">Set as Default Profile</Label>
              <Switch
                checked={newProfile.is_default}
                onCheckedChange={(val) => setNewProfile({ ...newProfile, is_default: val })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-stone-600 text-stone-300 hover:bg-stone-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Profile
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Profile Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
              Assign Compliance Profile
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-stone-700/50 rounded-lg">
                <p className="text-sm text-stone-400">Company</p>
                <p className="text-white font-medium">{selectedCompany.name}</p>
              </div>
              
              <div>
                <Label className="text-stone-300">Select Compliance Profile</Label>
                <div className="space-y-2 mt-2">
                  {profiles.map((profile) => (
                    <button
                      key={profile.id}
                      onClick={() => handleAssignCompliance(selectedCompany.id, profile.id)}
                      disabled={assigning}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        selectedCompany.compliance_profile_id === profile.id
                          ? "border-emerald-500 bg-emerald-500/10"
                          : "border-stone-600 hover:border-stone-500 hover:bg-stone-700/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{profile.name}</p>
                          <p className="text-sm text-stone-400">{profile.code}</p>
                        </div>
                        {selectedCompany.compliance_profile_id === profile.id && (
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
